import * as React from 'react';
import {Alert, Button, Form, Modal, Radio} from 'antd';
import {
  InventoryStockItemStateFilter,
  InventoryStockItemStateTargetType,
  Item,
  STATE_TARGET_INTERNALLY_USED,
  STATE_TARGET_MAINTENANCE,
  STATE_TARGET_USER,
  STATE_TARGET_WAREHOUSE,
  User,
  Warehouse
} from '../api/dtos/dtos';
import locales from '../locales';
import ItemSelector from '../item/ItemSelector';
import {RentalState} from '../common/state';
import AutoCompleteFormItem from "../common/form-items/AutoCompleteFormItem";
import {FormComponentProps} from "antd/lib/form";
import {SelectValue} from "antd/lib/select";
import HiddenFormItem from "../common/form-items/HiddenFormItem";

const FIELD_MOVE_USER = 'moveUser';
const FIELD_MOVE_WAREHOUSE = 'moveWarehouse';
const FIELD_MOVE_ITEM = 'moveItem';

interface Props extends FormComponentProps {
  users: User[];
  warehouses: Warehouse[];
  onOk: (value: InventoryStockItemStateFilter) => void;
  onCancel?: () => void;
  okText?: string;
}

interface State {
  targetType?: InventoryStockItemStateTargetType;
  itemSelectorVisible: boolean;
  filteredUsers: User[];
  filteredWarehouses: Warehouse[];
  selectedItem?: Item;
}

class InventoryStockItemStateSelector extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      targetType: undefined,
      itemSelectorVisible: false,
      filteredUsers: this.props.users,
      filteredWarehouses: this.props.warehouses,
      selectedItem: undefined,
    };
  }

  public componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
    if (prevProps.users !== this.props.users || prevProps.warehouses !== this.props.warehouses) {
      this.setState({
        filteredUsers: this.props.users,
        filteredWarehouses: this.props.warehouses,
      })
    }
  }

  public render(): React.ReactNode {
    const {form} = this.props;
    const {itemSelectorVisible, selectedItem, filteredUsers, filteredWarehouses, targetType} = this.state;

    return (
      <Form>
        <div style={{marginBottom: 16, display: 'flex', justifyContent: 'center'}}>
          <Radio.Group value={targetType} onChange={this.onModeChange}>
            <Radio value={STATE_TARGET_USER}>{locales.user}</Radio>
            <Radio value={STATE_TARGET_WAREHOUSE}>{locales.warehouse}</Radio>
            <Radio value={STATE_TARGET_INTERNALLY_USED}>{locales.internally_used}</Radio>
          </Radio.Group>
        </div>

        {targetType === STATE_TARGET_WAREHOUSE && (
          <AutoCompleteFormItem
            form={form}
            fieldId={FIELD_MOVE_WAREHOUSE}
            label={locales.warehouse}
            initialValue={undefined}
            allowClear={true}
            required={true}
            onChange={this.onWarehouseSearchChange}
            dataSource={filteredWarehouses.map(w => w.name)}
            validateOnDataSource={true}
          />
        )}

        {targetType === STATE_TARGET_USER && (
          <AutoCompleteFormItem
            form={form}
            fieldId={FIELD_MOVE_USER}
            label={locales.username}
            initialValue={undefined}
            allowClear={true}
            required={true}
            onChange={this.onUserSearchChange}
            dataSource={filteredUsers.map(u => u.userName || '')}
            validateOnDataSource={true}
          />
        )}

        {(targetType === STATE_TARGET_INTERNALLY_USED || targetType === STATE_TARGET_MAINTENANCE) && (
          <HiddenFormItem
            form={form}
            fieldId={FIELD_MOVE_ITEM}
            label={locales.selected_item}
            initialValue={selectedItem ? selectedItem.id : undefined}
            required={false}
          >
            {!selectedItem && (
              <Button icon="search" onClick={this.openItemSelector}>
                {locales.select_item}
              </Button>
            )}
            {selectedItem && (
              <Alert
                type="info"
                message={selectedItem.name}
                closable={true}
                onClose={this.clearSelectedItem}
              />
            )}
          </HiddenFormItem>
        )}

        <div style={{display: 'flex', gap: 8, justifyContent: 'flex-end'}}>
          {this.props.onCancel && (
            <Button onClick={this.props.onCancel}>{locales.cancel}</Button>
          )}
          {this.props.onOk && (
            <Button type="primary" onClick={this.onOkClick}>{this.props.okText || locales.save}</Button>
          )}
        </div>

        {itemSelectorVisible && (
          <Modal
            visible={true}
            title={locales.select_item}
            footer={null}
            onCancel={this.closeItemSelector}
            width="auto"
            style={{margin: '0 24px'}}
            destroyOnClose={true}
          >
            <ItemSelector
              allowedStates={[RentalState.RENTED, RentalState.AVAILABLE]}
              selectedStates={[RentalState.RENTED, RentalState.AVAILABLE]}
              excludedFromSearch={[]}
              onSelect={this.onItemSelected}
            />
          </Modal>
        )}
      </Form>
    );
  }

  private onOkClick = () => {
    const {form, warehouses, users} = this.props;
    const {targetType, selectedItem} = this.state;

    if (targetType === undefined) {
      return;
    }


    const request: InventoryStockItemStateFilter = {
      targetType
    };

    if (targetType === STATE_TARGET_MAINTENANCE || targetType === STATE_TARGET_INTERNALLY_USED) {
      request.item = selectedItem;
    }

    if (targetType === STATE_TARGET_USER) {
      const userId = form.getFieldValue(FIELD_MOVE_USER);
      if (users.find(u => u.userName === userId) !== undefined) {
        request.userId = userId;
      } else {
        form.setFieldsValue({[FIELD_MOVE_USER]: null});
        this.setState({
          targetType,
          filteredUsers: this.props.users,
        });
      }
    }

    if (targetType === STATE_TARGET_WAREHOUSE) {
      const warehouseName = form.getFieldValue(FIELD_MOVE_WAREHOUSE);
      const selected = warehouses.find(w => w.name === warehouseName)
      if (selected !== undefined) {
        request.warehouseId = selected.id;
      } else {
        form.setFieldsValue({[FIELD_MOVE_WAREHOUSE]: null});
        this.setState({
          targetType,
          filteredWarehouses: this.props.warehouses,
        });
      }
    }

    form.validateFieldsAndScroll((err, values) => {
      if (!err) {
        this.props.onOk(request);
      }
    });
  };

  private onModeChange = (e: any) => {
    const mode: InventoryStockItemStateTargetType = e.target.value;
    this.props.form.resetFields([FIELD_MOVE_USER, FIELD_MOVE_WAREHOUSE]);
    this.setState({
      targetType: mode,
      selectedItem: undefined,
      filteredUsers: this.props.users,
      filteredWarehouses: this.props.warehouses,
    });


  };

  private onUserSearchChange = (v: SelectValue) => {
    const {users} = this.props;
    const value = v != null ? String(v) : '';

    this.setState({
      filteredUsers: users.filter(u => u.userName && u.userName.toLowerCase().includes(value.toLowerCase())),
    });
  };

  private onWarehouseSearchChange = (v: SelectValue) => {
    const {warehouses} = this.props;

    const value = v != null ? String(v) : '';

    this.setState({
      filteredWarehouses: warehouses.filter(w => w.name && w.name.toLowerCase().includes(value.toLowerCase())),
    });
  };


  private openItemSelector = () => this.setState({itemSelectorVisible: true});

  private closeItemSelector = () => this.setState({itemSelectorVisible: false});

  private onItemSelected = (item: Item) => {
    this.setState({selectedItem: item, itemSelectorVisible: false});
  };

  private clearSelectedItem = () => {
    this.setState({selectedItem: undefined});
  };
}

export default Form.create<Props>()(InventoryStockItemStateSelector);



// WEBPACK FOOTER //
// ./src/inventory-stock-details/InventoryStockItemStateSelector.tsx